import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Activity, AlertTriangle, CheckCircle2, RefreshCw, Loader2, Rocket, Users,
} from 'lucide-react';
import api from '../utils/api';

/**
 * Iter-198 — Service-Worker Version Distribution (Admin Ops Widget).
 *
 * Pulls /api/admin/client-events/sw-version-distribution. Ops-only —
 * not user-facing. Designed to spot:
 *   • Users stuck on stale SW builds after a deploy
 *   • Refresh-toast adoption (Refresh vs Dismiss ratio)
 *   • The "did the latest version actually reach everyone" question
 */
const SwVersionDistributionCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const res = await api.get('/admin/client-events/sw-version-distribution', {
        params: { lookback_hours: 24, stale_threshold_hours: 6 },
      });
      setData(res.data);
    } catch (_) {
      // Silently — non-admins won't see this card at all.
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const refresh = () => { setRefreshing(true); load(); };

  if (loading) {
    return (
      <Card data-testid="sw-version-card-loading">
        <CardHeader><CardTitle className="text-sm">Service-Worker Versions</CardTitle></CardHeader>
        <CardContent><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const alert = data.alert;
  const dist = Array.isArray(data.distribution) ? data.distribution : [];
  const adoption = data.refresh_adoption || {};

  return (
    <Card data-testid="sw-version-card" className={
      alert?.severity === 'critical' ? 'border-rose-300'
        : alert?.severity === 'warning' ? 'border-amber-300'
        : 'border-emerald-200'
    }>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          {alert ? <AlertTriangle className="w-4 h-4 text-amber-600" /> : <Activity className="w-4 h-4 text-cyan-600" />}
          Service-Worker Versions
          <span className="text-[10px] font-normal opacity-60">(last {data.lookback_hours}h)</span>
        </CardTitle>
        <Button
          variant="outline" size="sm"
          onClick={refresh} disabled={refreshing}
          data-testid="sw-version-refresh-btn"
        >
          {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
          Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Top-line stats */}
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Badge variant="outline" className="border-cyan-300 text-cyan-800" data-testid="sw-version-latest-badge">
            <Rocket className="w-3 h-3 mr-1" />
            Latest: {data.latest_version || 'unknown'}
          </Badge>
          <Badge variant="outline" className="border-gray-300 text-gray-700" data-testid="sw-version-total-users">
            <Users className="w-3 h-3 mr-1" />
            {data.total_users_seen || 0} unique user{data.total_users_seen === 1 ? '' : 's'}
          </Badge>
          {data.last_deployed_at && (
            <Badge variant="outline" className="border-sky-300 text-sky-800" data-testid="sw-version-deployed">
              Deployed: {new Date(data.last_deployed_at).toLocaleString()}
            </Badge>
          )}
        </div>

        {/* Stale-version alert */}
        {alert && (
          <div
            className={`text-xs rounded-md px-3 py-2 border ${
              alert.severity === 'critical'
                ? 'bg-rose-50 border-rose-200 text-rose-900'
                : 'bg-amber-50 border-amber-200 text-amber-900'
            }`}
            data-testid="sw-version-alert"
          >
            <span className="font-semibold mr-1">⚠️ {alert.severity.toUpperCase()}:</span>
            {alert.message}
          </div>
        )}

        {/* Version distribution bars */}
        {dist.length === 0 ? (
          <p className="text-xs text-gray-500" data-testid="sw-version-empty">
            No SW telemetry yet. Events flow in as users load the app.
          </p>
        ) : (
          <div className="space-y-1.5" data-testid="sw-version-dist-list">
            {dist.map((row) => (
              <div key={row.version} className="flex items-center gap-2 text-xs" data-testid={`sw-version-row-${row.version}`}>
                <div className="w-14 font-mono font-semibold shrink-0 flex items-center gap-1">
                  {row.version}
                  {row.is_latest && <CheckCircle2 className="w-3 h-3 text-emerald-600" />}
                </div>
                <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-full ${row.is_latest ? 'bg-emerald-500' : 'bg-amber-500'}`}
                    style={{ width: `${row.pct}%` }}
                  />
                </div>
                <div className="w-24 text-right text-gray-600 shrink-0">
                  {row.unique_users} user{row.unique_users === 1 ? '' : 's'} ({row.pct}%)
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Refresh-toast adoption */}
        <div className="border-t pt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-600" data-testid="sw-version-adoption">
          <span>Toasts shown: <strong>{adoption.toasts_shown || 0}</strong></span>
          <span className="text-emerald-700">Refresh accepted: <strong>{adoption.refreshes_accepted || 0}</strong></span>
          <span className="text-gray-500">Dismissed: <strong>{adoption.refreshes_dismissed || 0}</strong></span>
          {adoption.acceptance_pct != null && (
            <span className="font-semibold">Adoption: <span className={adoption.acceptance_pct >= 50 ? 'text-emerald-700' : 'text-amber-700'}>{adoption.acceptance_pct}%</span></span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SwVersionDistributionCard;
